import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useT, useFieldError } from '@kmgestao/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  toast,
} from '@kmgestao/ui';

import {
  buildProviderFormSchema,
  useUpsertProvider,
  type ProviderFormInput,
  type ProviderRow,
} from '../../../../features/ai-admin/index.js';

export interface ProviderFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `null` = criar; objeto = editar. */
  editing: ProviderRow | null;
}

export function ProviderForm({ open, onOpenChange, editing }: ProviderFormProps) {
  const { t } = useT('ai-admin');
  const { t: tCommon } = useT('common');
  const tError = useFieldError();
  const upsert = useUpsertProvider();

  const isEditing = editing !== null;
  const schema = useMemo(() => buildProviderFormSchema({ requireApiKey: !isEditing }), [isEditing]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ProviderFormInput>({
    resolver: zodResolver(schema),
    values: {
      displayName: editing?.displayName ?? '',
      modelId: editing?.primaryModel?.modelId ?? '',
      baseUrl: editing?.baseUrl ?? '',
      apiKey: '',
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await upsert.mutateAsync({
        id: editing?.id ?? null,
        displayName: values.displayName,
        baseUrl: values.baseUrl,
        modelId: values.modelId,
        apiKey: values.apiKey.length > 0 ? values.apiKey : null,
      });
      toast.success(
        isEditing ? t('providerForm.success.updated') : t('providerForm.success.created'),
      );
      reset();
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : isEditing
            ? t('providerForm.errors.updateFailed')
            : t('providerForm.errors.createFailed');
      toast.error(message);
    }
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) reset();
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('providerForm.titleEdit') : t('providerForm.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('providerForm.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="displayName">{t('providerForm.fields.displayName.label')}</Label>
            <Input
              id="displayName"
              placeholder={t('providerForm.fields.displayName.placeholder')}
              autoComplete="off"
              hasError={!!errors.displayName}
              {...register('displayName')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.displayName) ?? t('providerForm.fields.displayName.hint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="modelId">{t('providerForm.fields.modelId.label')}</Label>
            <Input
              id="modelId"
              placeholder={t('providerForm.fields.modelId.placeholder')}
              autoComplete="off"
              hasError={!!errors.modelId}
              {...register('modelId')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.modelId) ?? t('providerForm.fields.modelId.hint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="baseUrl">{t('providerForm.fields.baseUrl.label')}</Label>
            <Input
              id="baseUrl"
              type="url"
              inputMode="url"
              placeholder={t('providerForm.fields.baseUrl.placeholder')}
              autoComplete="off"
              hasError={!!errors.baseUrl}
              {...register('baseUrl')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.baseUrl) ?? t('providerForm.fields.baseUrl.hint')}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="apiKey">{t('providerForm.fields.apiKey.label')}</Label>
            <Input
              id="apiKey"
              type="password"
              autoComplete="new-password"
              placeholder={t('providerForm.fields.apiKey.placeholder')}
              hasError={!!errors.apiKey}
              {...register('apiKey')}
            />
            <p className="text-sm text-muted-foreground">
              {tError(errors.apiKey) ??
                (isEditing
                  ? t('providerForm.fields.apiKey.hintEdit')
                  : t('providerForm.fields.apiKey.hintCreate'))}
            </p>
          </div>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                {tCommon('actions.cancel')}
              </Button>
            </DialogClose>
            <Button type="submit" disabled={upsert.isPending}>
              {upsert.isPending
                ? tCommon('status.saving')
                : isEditing
                  ? t('providerForm.submit.edit')
                  : t('providerForm.submit.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
